import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms'
import { BasicFieldType, EmailFieldTypes } from '@app/v2/shared/enums/field.enum'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'
import { AppService } from '@app/v2/shared/services/app.service'
import { EmailValidator } from '@app/v2/shared/validators/email.validator'
import { TranslateModule } from '@ngx-translate/core'
import { Observable, debounceTime, distinctUntilChanged, filter, map, of, take } from 'rxjs'
import { BasicInputComponent } from '../basic-input/basic-input.component'

@Component({
  selector: 'email-input',
  templateUrl: './email-input.component.html',
  styleUrls: [],
  standalone: true,
  imports: [BasicInputComponent, TranslateModule]
})
export class EmailInputComponent implements OnInit {
  // Form Control for input field
  @Input() control!: FormControl
  // Label for input field
  @Input() label: string = 'Email address'
  // placeholder for input field
  @Input() placeholder: string = ''
  // Icon for input field
  @Input() icon: string = ''
  // IconType for input field
  @Input() iconType: string = 'fill'
  // Id for input field
  @Input() id: string = 'email-input'
  // Maximum characters allowed for input field
  @Input() maxCharacters: number = 254
  // Flag for input field to load
  @Input() isLoading: boolean = false
  // Flag for input field to show error
  @Input() verifyDomain: boolean = false
  // Flag for input field to show required
  @Input() required: boolean = false
  @Input() mode: BasicInputMode = BasicInputMode.DEFAULT
  // Flag to show clear icon for email input
  @Input() allowClearButton: boolean = true

  // Emit value for parent component
  @Output() onInputBlur: EventEmitter<string> = new EventEmitter<string>()
  @Output() onKeydown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>()
  @Output() onBlurValueChange: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>()

  public basicFieldType = BasicFieldType.EMAIL

  constructor(private _appService: AppService) {}

  private _lastUsedValue!: string
  private readonly _destroyRef = inject(DestroyRef)

  ngOnInit(): void {
    this.control.addValidators([(control) => EmailValidator.validate(control, [], EmailFieldTypes.ALLOW_ALL, this.required), Validators.pattern(/^[^@\s]+@[a-zA-Z0-9\-_.]+$/)])
    if (this.verifyDomain) this.control.addAsyncValidators(this.validateDomain.bind(this))
    // Get last used value
    if (!this.control.value) {
      this.control.valueChanges.pipe(take(1), takeUntilDestroyed(this._destroyRef)).subscribe((currentValue: string) => (this._lastUsedValue = currentValue))
    } else this._lastUsedValue = this.control.value

    this._activateValidationOnUserInput()
  }

  /**
   * Activates validation on user input
   * @returns void
   */
  private _activateValidationOnUserInput(): void {
    if (this.control.dirty && !this.control.touched) {
      this.control.markAsTouched()
      return
    }
    this.control.valueChanges
      .pipe(
        filter(() => this.control.dirty && !this.control.touched),
        take(1),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => this.control.markAsTouched())
  }

  /**
   * @async validateDomain
   * @description Validates domain of email address entered after 500ms
   * @param {AbstractControl} control
   * @returns {Observable<ValidationErrors | null>}
   */
  validateDomain(control: AbstractControl): Observable<ValidationErrors | null> {
    if (!control.value) return of(null)

    return this._appService.checkForEmailDomain(control.value).pipe(
      debounceTime(500),
      distinctUntilChanged(),
      map((response: any) => (response.data['valid_domain'] ? null : { domainInvalid: true }))
    )
  }

  /**
   * Emits control value when blur event is fired
   * @returns void
   */
  public onBlur(): void {
    this.onInputBlur.emit(this.control.value)
  }

  /**
   * Forward keydown event to parent
   * @param {KeyboardEvent} event
   */
  public onKeydownEvent(event: KeyboardEvent): void {
    this.onKeydown.emit(event)
  }

  /**
   * Forward blur event to parent
   * @param {FocusEvent} event
   */
  public onBlurEvent(event: FocusEvent): void {
    this.onBlurValueChange.emit(event)
  }
}
